import { resetLocalAuthKey, setLocalAuthKey } from '@/common/auth';
import { StandardButton } from '@/components/buttons/standard';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Title } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { SignupForm, SignupFormValues } from './form';
import { useCustomCreateUserMutation } from './graphql.generated';
import { SignupHeader } from './header/component';

export const SignupPage = () => {
	const navigate = useNavigate();
	const [customCreateUser, { error, loading: creatingUser }] = useCustomCreateUserMutation();
	const ref = useRef<FormikProps<SignupFormValues>>(null);
	const signup = useCallback(() => {
		ref.current?.submitForm();
	}, []);
	const signupButton = useMemo(
		() => (
			<StandardButton loading={creatingUser} onClick={signup}>
				Sign Up
			</StandardButton>
		),
		[signup, creatingUser]
	);

	const homeButton = useMemo(
		() => <StandardButton onClick={() => navigate({ to: '/home' })}>Home</StandardButton>,
		[navigate]
	);

	const handleSubmit = useCallback(
		async (values: SignupFormValues) => {
			try {
				resetLocalAuthKey();
				// TODO: handle the scenario where the user already exists (via email address)
				const { data } = await customCreateUser({
					variables: {
						input: {
							email: values.email.toLowerCase(),
							firstName: values.firstName,
							lastName: values.lastName,
							password: values.password,
							confirm: values.reenterPassword,
						},
					},
				});

				if (data?.customCreateUser?.authToken) {
					setLocalAuthKey(data.customCreateUser.authToken);
					navigate({ to: '/signup-details' });
				}
			} catch (error) {
				if ((error as Error).message === 'User already exists') {
					navigate({
						to: '/request-password-reset',
						search: { email: values.email.toLowerCase() },
					});
				} else {
					console.error(error);
				}
			}
		},
		[customCreateUser, navigate]
	);

	return (
		<PrimaryLayout footer={{ children: [error ? homeButton : signupButton] }}>
			<Flex direction="column" justify="space-between" h="100%" w="100%">
				<Flex direction="column" align="center" justify="flex-start" w="100%">
					<SignupHeader />
					{error ? (
						<Title order={2} ta="center" mt="xl">
							An error occurred, please try again later or contact support.
						</Title>
					) : (
						<SignupForm onSubmit={handleSubmit} formRef={ref} />
					)}
				</Flex>
			</Flex>
		</PrimaryLayout>
	);
};
